import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import { ProfileService } from '@outlaw/core';
import { FollowerService } from '../../services/api/followers.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';
import { FollowButtonProfile } from '../../interfaces';
import { IconComponent } from '@outlaw/icons';

type FollowButtonSize = 'small' | 'normal';

@Component({
  selector: 'app-follow-button',
  templateUrl: './follow-button.component.html',
  styleUrls: ['./follow-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
})
export class FollowButtonComponent {
  private profileService = inject(ProfileService);
  private followService = inject(FollowerService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  profile = input.required<FollowButtonProfile>();
  size = input<FollowButtonSize>('normal');

  followStatusChanged = output<boolean>();
  showGuestPopup = output<void>();

  $profile = linkedSignal(() => this.profile());

  isLoading = signal(false);

  followButtonClick(): void {
    if (!this.profileService.getUser()) {
      this.showGuestPopup.emit();
    } else {
      this.followEvent();
    }
  }

  followEvent(): void {
    if (this.isLoading()) return;

    this.isLoading.set(true);

    const action = this.profile().is_following
      ? this.followService.unfollowUser(this.profile().passport_uuid)
      : this.followService.followUser(this.profile().passport_uuid);

    action
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
          this.cdr.detectChanges();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.$profile.update(profile => ({
            ...profile,
            is_following: !profile.is_following,
          }));
          this.followStatusChanged.emit(this.$profile().is_following);
          this.cdr.detectChanges();
        },
        error: error => {
          console.error('Failed to follow user', error);
        },
      });
  }
}
